<div class="table-container">
  <uikit-async-ui
    [data]="displayedOpportunities$ | async"
    [error]="loadingError$ | async"
    [loading]="loadingOpportunities$ | async"
  >
    <ng-container loading>
      <uikit-list-stencil [showHeader]="true" numRows="25"></uikit-list-stencil>
    </ng-container>

    <ng-container *ngTemplateOutlet="errorState" error></ng-container>

    <ng-container *successData="let data">
      <ng-container
        *ngTemplateOutlet="data?.length <= 0 ? emptyState : tableRows; context: { opportunities: data }"
      ></ng-container>
    </ng-container>
  </uikit-async-ui>
</div>

<mat-paginator
  #paginator
  (page)="pageUpdated($event)"
  [disabled]="disablePagination$ | async"
  [length]="totalItems$ | async"
  [pageSize]="PAGE_SIZE"
  [showFirstLastButtons]="false"
></mat-paginator>

<ng-template #emptyState>
  <glxy-empty-state>
    <glxy-empty-state-hero>
      <img
        src="https://vstatic-prod.apigateway.co/sales-center-client..prod/assets/opportunities-empty-states/no-opportunities-found.svg"
      />
    </glxy-empty-state-hero>
    <glxy-empty-state-title>
      {{ 'FRONTEND.SALES_UI.PIPELINE.FILTERS_RETURNED_ZERO_RESULTS_TITLE' | translate }}
    </glxy-empty-state-title>
    <p>
      {{ 'FRONTEND.SALES_UI.PIPELINE.FILTERS_RETURNED_ZERO_RESULTS_DESCRIPTION' | translate }}
    </p>
  </glxy-empty-state>
</ng-template>

<ng-template #errorState>
  <glxy-empty-state>
    <glxy-empty-state-title>
      {{ 'FRONTEND.SALES_UI.PIPELINE.TABLE.ERRORS.FETCH_OPPORTUNITIES_ERROR_TITLE' | translate }}
    </glxy-empty-state-title>
    {{ 'FRONTEND.SALES_UI.PIPELINE.TABLE.ERRORS.FETCH_OPPORTUNITIES_ERROR_DESCRIPTION' | translate }}
  </glxy-empty-state>
</ng-template>

<ng-template #tableRows let-opportunities="opportunities">
  <table
    (matSortChange)="sortChanged($event)"
    [dataSource]="opportunities"
    [matSortActive]="sortActive$ | async"
    [matSortDirection]="sortDir$ | async"
    class="table"
    mat-table
    matSort
    matSortStart="desc"
  >
    <ng-container *ngIf="opportunityMetaTemplateData$ | async as templateData">
      <ng-container matColumnDef="{{ COLUMNS.OPPORTUNITY }}">
        <th *matHeaderCellDef class="table-cell--medium" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.OPPORTUNITY' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--medium" mat-cell>
          <ng-container *ngIf="opportunity.opportunityUrl; else noUrlTemplate">
            <a [queryParams]="{ origin: 'pipeline' }" [routerLink]="opportunity.opportunityUrl">
              {{ opportunity.name }}
            </a>
          </ng-container>
          <ng-template #noUrlTemplate>
            <span>{{ opportunity.name }}</span>
          </ng-template>
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.ACCOUNT }}">
        <th *matHeaderCellDef class="table-cell--medium" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.ACCOUNT' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--medium" mat-cell>
          <uikit-async-ui
            [data]="opportunity.accountGroupId | getAccountName | async"
            [error]="(opportunity.accountGroupId | isAccountDeleted | async) === true"
            [loading]="opportunity.accountGroupId | isAccountLoading | async"
          >
            <ng-container *successData="let account">
              <a [queryParams]="{ origin: 'pipeline' }" [routerLink]="opportunity.accountUrl">
                {{ account }}
              </a>
            </ng-container>
            <ng-container loading>
              <mat-spinner diameter="16"></mat-spinner>
            </ng-container>
            <ng-container error>
              <span>{{ 'FRONTEND.SALES_UI.PIPELINE.ACCOUNT_DELETED' | translate }}</span>
            </ng-container>
          </uikit-async-ui>
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.PIPELINE_NAME }}">
        <th *matHeaderCellDef class="table-cell--medium" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.PIPELINE_NAME' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--medium" mat-cell>
          <span *ngIf="userPipelines$ | async">
            {{
              (opportunity.pipelineId | getPipelineFromId : (userPipelines$ | async))?.name ||
                'FRONTEND.SALES_UI.PIPELINE.PIPELINE_NOT_FOUND' | translate
            }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.PIPELINE_STAGE }}">
        <th *matHeaderCellDef mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.STAGE' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--small" mat-cell>
          <span *ngIf="userPipelines$ | async">
            {{
              (
                opportunity.pipelineId
                | getPipelineFromId : (userPipelines$ | async)
                | pipelineStage : opportunity.probability
              )?.name || 'FRONTEND.SALES_UI.COMMON.NOT_APPLICABLE_LABEL' | translate
            }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.TYPE }}">
        <th *matHeaderCellDef mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.TYPE' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small align-left" mat-cell>
          <span *ngIf="availableTypes$ | async as availableTypes">
            {{
              (opportunity.type ?? '' | getTypeNameFromId : availableTypes)?.displayName ||
                'FRONTEND.SALES_UI.COMMON.NOT_APPLICABLE_LABEL' | translate
            }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.POTENTIAL }}">
        <th
          *matHeaderCellDef
          arrowPosition="before"
          class="table-cell--extra-small align-right"
          mat-header-cell
          mat-sort-header="{{ COLUMNS.POTENTIAL }}"
        >
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.POTENTIAL' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small align-right" mat-cell>
          {{ opportunity.potentialRevenue || 0 | formatCurrency : templateData.preferredCurrency }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.MEDDIC }}">
        <th *matHeaderCellDef mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.QUALIFICATION' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small align-right" mat-cell>
          {{ opportunity.score }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.MONTHLY_REVENUE }}">
        <th *matHeaderCellDef class="table-cell--extra-small align-right" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.MONTHLY_REVENUE' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small align-right" mat-cell>
          {{ opportunity.monthlyRevenue || 0 | formatCurrency : templateData.preferredCurrency }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.STATUS }}">
        <th *matHeaderCellDef class="table-cell--extra-small" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.STATUS' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small" mat-cell>
          {{ opportunity.status }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.CREATED }}">
        <th
          *matHeaderCellDef
          arrowPosition="before"
          class="table-cell--small align-right"
          mat-header-cell
          mat-sort-header="{{ COLUMNS.CREATED }}"
        >
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.CREATED_DATE' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--small align-right table-cell--dbl-date" mat-cell>
          {{ opportunity.createdDate | date : 'mediumDate' }}
          <mat-card-subtitle>
            {{ opportunity.createdDate | daysAgo }}
          </mat-card-subtitle>
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.EXPECTED_CLOSE }}">
        <th
          *matHeaderCellDef
          arrowPosition="before"
          class="table-cell--small align-right"
          mat-header-cell
          mat-sort-header="{{ COLUMNS.EXPECTED_CLOSE }}"
        >
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.EXPECTED_CLOSE' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--small align-right table-cell--dbl-date" mat-cell>
          {{
            (opportunity.expectedCloseDate | date : 'mediumDate') ||
              ('FRONTEND.SALES_UI.COMMON.NOT_APPLICABLE_LABEL' | translate)
          }}
          <mat-card-subtitle *ngIf="opportunity.expectedCloseDate">
            <sales-common-days-away
              [date]="opportunity.expectedCloseDate"
              [disableColor]="opportunity.status === CLOSEDWON || opportunity.status === CLOSEDLOST"
            ></sales-common-days-away>
          </mat-card-subtitle>
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.ACTUAL_CLOSE }}">
        <th
          *matHeaderCellDef
          arrowPosition="before"
          class="table-cell--small align-right"
          mat-header-cell
          mat-sort-header="{{ COLUMNS.ACTUAL_CLOSE }}"
        >
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.ACTUAL_CLOSE' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--small align-right" mat-cell>
          {{
            (opportunity.actualCloseDate | date : 'mediumDate') ||
              ('FRONTEND.SALES_UI.COMMON.NOT_APPLICABLE_LABEL' | translate)
          }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.ASSIGNEE }}">
        <th *matHeaderCellDef class="table-cell--small" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.COMMON.ASSIGNEE' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--small" mat-cell>
          {{ opportunity.assigneeName }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.ASSIGNED_DATE }}">
        <th
          *matHeaderCellDef
          arrowPosition="before"
          class="table-cell--small align-right"
          mat-header-cell
          mat-sort-header="{{ COLUMNS.ASSIGNED_DATE }}"
        >
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.ASSIGNED' | translate }}
        </th>
        <td
          *matCellDef="let opportunity"
          [ngClass]="{ 'table-cell--dbl-date': !!opportunity.assignedDate }"
          class="table-cell--small align-right"
          mat-cell
        >
          {{ (opportunity.assignedDate | date : 'mediumDate') || '-' }}
          <mat-card-subtitle *ngIf="opportunity.assignedDate">
            {{ opportunity.assignedDate | daysAgo }}
          </mat-card-subtitle>
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.CALLS }}">
        <th *matHeaderCellDef class="table-cell--extra-small align-right" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.CALLS' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small align-right" mat-cell>
          {{ templateData.efforts ? templateData.efforts[opportunity.id]?.efforts['call_efforts'] || 0 : 0 }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.EMAILS }}">
        <th *matHeaderCellDef class="table-cell--extra-small align-right" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.COMMON.EMAILS' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small align-right" mat-cell>
          {{ templateData.efforts ? templateData.efforts[opportunity.id]?.efforts['email_efforts'] || 0 : 0 }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.MEETINGS }}">
        <th *matHeaderCellDef class="table-cell--extra-small align-right" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.COMMON.MEETINGS' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small align-right" mat-cell>
          {{ templateData.efforts ? templateData.efforts[opportunity.id]?.efforts['meetings_efforts'] || 0 : 0 }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.TOTAL_EFFORTS }}">
        <th *matHeaderCellDef class="table-cell--extra-small align-right" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.TOTAL_EFFORTS' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small align-right" mat-cell>
          {{
            (templateData.efforts ? templateData.efforts[opportunity.id]?.efforts['meetings_efforts'] || 0 : 0) +
              (templateData.efforts ? templateData.efforts[opportunity.id]?.efforts['email_efforts'] || 0 : 0) +
              (templateData.efforts ? templateData.efforts[opportunity.id]?.efforts['call_efforts'] || 0 : 0)
          }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.CONNECTIONS }}">
        <th *matHeaderCellDef class="table-cell--extra-small align-right" mat-header-cell>
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.CONNECTIONS' | translate }}
        </th>
        <td *matCellDef="let opportunity" class="table-cell--extra-small align-right" mat-cell>
          {{ templateData.efforts ? templateData.efforts[opportunity.id]?.efforts['connections'] || 0 : 0 }}
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.LAST_ACTIVITY }}">
        <th
          *matHeaderCellDef
          arrowPosition="before"
          class="table-cell--small align-right"
          mat-header-cell
          mat-sort-header="{{ COLUMNS.LAST_ACTIVITY }}"
        >
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.LAST_ACTIVITY' | translate }}
        </th>
        <td
          *matCellDef="let opportunity"
          [ngClass]="{ 'table-cell--dbl-date': !!opportunity.lastActivityDate }"
          class="table-cell--small align-right"
          mat-cell
        >
          {{
            (opportunity.lastActivityDate | date : 'mediumDate') || ('FRONTEND.SALES_UI.COMMON.DATES.NEVER' | translate)
          }}
          <mat-card-subtitle *ngIf="opportunity.lastActivityDate">
            {{ opportunity.lastActivityDate | daysAgo }}
          </mat-card-subtitle>
        </td>
      </ng-container>

      <ng-container matColumnDef="{{ COLUMNS.LAST_CONNECTED }}">
        <th
          *matHeaderCellDef
          arrowPosition="before"
          class="table-cell--small align-right"
          mat-header-cell
          mat-sort-header="{{ COLUMNS.LAST_CONNECTED }}"
        >
          {{ 'FRONTEND.SALES_UI.PIPELINE.COLUMN_LABELS.LAST_CONNECTED' | translate }}
        </th>
        <td
          *matCellDef="let opportunity"
          [ngClass]="{
            'table-cell--dbl-date': !!opportunity.lastConnectedDate
          }"
          class="table-cell--small align-right"
          mat-cell
        >
          {{
            (opportunity.lastConnectedDate | date : 'mediumDate') ||
              ('FRONTEND.SALES_UI.COMMON.DATES.NEVER' | translate)
          }}
          <mat-card-subtitle *ngIf="opportunity.lastConnectedDate">
            {{ opportunity.lastConnectedDate | daysAgo }}
          </mat-card-subtitle>
        </td>
      </ng-container>
    </ng-container>

    <tr *matHeaderRowDef="displayedColumns$ | async; sticky: true" mat-header-row></tr>
    <tr *matRowDef="let row; columns: displayedColumns$ | async" mat-row></tr>
  </table>
</ng-template>
